/**
 * Institutional Users Tab - Manage delegated users in institutional tenants
 *
 * Features:
 * - Stats banner (active/departed users)
 * - Delegated users list with status badges
 * - Add Employee button
 * - Depart / Reinstate actions
 * - Succession transfer for departed users
 * - Succession log viewer
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  InstitutionalStats,
  InstitutionalUser,
  SuccessionLogEntry,
  CreateDelegatedUserRequest,
} from '../../types/institutional';
import apiClient from '../../services/api';

export default function InstitutionalUsersTab() {
  const { t } = useTranslation('admin');

  const [stats, setStats] = useState<InstitutionalStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Create user form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<CreateDelegatedUserRequest>({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'user',
  });
  const [isCreating, setIsCreating] = useState(false);

  // Succession
  const [successionUserId, setSuccessionUserId] = useState<number | null>(null);
  const [successorId, setSuccessorId] = useState<string>('');
  const [isTransferring, setIsTransferring] = useState(false);

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.get<InstitutionalStats>('/admin/institutional/stats');
      setStats(response.data);
    } catch {
      setError(t('institutional.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setMessage(null);
    try {
      await apiClient.post('/admin/institutional/users', createForm);
      setMessage({ type: 'success', text: t('institutional.userCreated') });
      setShowCreateForm(false);
      setCreateForm({ username: '', email: '', full_name: '', password: '', role: 'user' });
      await loadStats();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMessage({ type: 'error', text: detail || t('institutional.createFailed') });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDepart = async (userId: number) => {
    setMessage(null);
    try {
      await apiClient.post(`/admin/institutional/users/${userId}/depart`);
      setMessage({ type: 'success', text: t('institutional.userDeparted') });
      await loadStats();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMessage({ type: 'error', text: detail || t('institutional.departFailed') });
    }
  };

  const handleReinstate = async (userId: number) => {
    setMessage(null);
    try {
      await apiClient.post(`/admin/institutional/users/${userId}/reinstate`);
      setMessage({ type: 'success', text: t('institutional.userReinstated') });
      await loadStats();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMessage({ type: 'error', text: detail || t('institutional.reinstateFailed') });
    }
  };

  const handleTransferSuccession = async () => {
    if (!successionUserId || !successorId) return;
    setIsTransferring(true);
    setMessage(null);
    try {
      const response = await apiClient.post<{ files_transferred: number; tasks_transferred: number }>(
        `/admin/institutional/users/${successionUserId}/succession`,
        { successor_user_id: parseInt(successorId) }
      );
      setMessage({
        type: 'success',
        text: t('institutional.successionComplete', {
          files: response.data.files_transferred,
          tasks: response.data.tasks_transferred,
        }),
      });
      setSuccessionUserId(null);
      setSuccessorId('');
      await loadStats();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMessage({ type: 'error', text: detail || t('institutional.successionFailed') });
    } finally {
      setIsTransferring(false);
    }
  };

  const getStatusBadge = (u: InstitutionalUser) => {
    if (u.user_type === 'institution') {
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 text-teal-700">{t('institutional.statusInstitution')}</span>;
    }
    switch (u.status) {
      case 'active':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">{t('institutional.statusActive')}</span>;
      case 'departed':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">{t('institutional.statusDeparted')}</span>;
      case 'blocked':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">{t('institutional.statusBlocked')}</span>;
      default:
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">{u.status}</span>;
    }
  };

  // Filter users: institution user, active persons, departed persons
  const institutionUser = stats?.users.find(u => u.user_type === 'institution');
  const activeUsers = stats?.users.filter(u => u.user_type === 'person' && u.status === 'active') || [];
  const departedUsers = stats?.users.filter(u => u.user_type === 'person' && u.status === 'departed') || [];
  const possibleSuccessors = stats?.users.filter(
    u => u.user_type !== 'institution' && u.status === 'active' && u.id !== successionUserId
  ) || [];
  // Include the institutional user as a possible successor target
  if (institutionUser) {
    possibleSuccessors.push(institutionUser);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={loadStats} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
          {t('agents.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* Stats Banner */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{stats?.active_users || 0}</div>
          <div className="text-sm text-gray-500">{t('institutional.activeEmployees')}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-amber-600">{stats?.departed_users || 0}</div>
          <div className="text-sm text-gray-500">{t('institutional.departedEmployees')}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-teal-600">{stats?.total_users || 0}</div>
          <div className="text-sm text-gray-500">{t('institutional.totalMembers')}</div>
        </div>
      </div>

      {/* Header + Add Employee */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('institutional.title')}</h2>
          <p className="text-sm text-gray-500">{t('institutional.description')}</p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
        >
          {showCreateForm ? t('common.cancel') : t('institutional.addEmployee')}
        </button>
      </div>

      {/* Create Employee Form */}
      {showCreateForm && (
        <form onSubmit={handleCreateUser} className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <h3 className="font-medium text-gray-900">{t('institutional.addEmployeeTitle')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('createUserForm.username')}</label>
              <input
                type="text"
                value={createForm.username}
                onChange={e => setCreateForm({ ...createForm, username: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('createUserForm.email')}</label>
              <input
                type="email"
                value={createForm.email}
                onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('createUserForm.fullName')}</label>
              <input
                type="text"
                value={createForm.full_name}
                onChange={e => setCreateForm({ ...createForm, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('createUserForm.password')}</label>
              <input
                type="password"
                value={createForm.password}
                onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                required
                minLength={8}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('createUserForm.role')}</label>
            <select
              value={createForm.role}
              onChange={e => setCreateForm({ ...createForm, role: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-white"
            >
              <option value="user">{t('users.user')}</option>
              <option value="admin">{t('users.admin')}</option>
            </select>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isCreating}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {isCreating ? t('common.loading') : t('institutional.createEmployee')}
            </button>
          </div>
        </form>
      )}

      {/* Active Users */}
      {activeUsers.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700">{t('institutional.activeEmployees')} ({activeUsers.length})</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {activeUsers.map(u => (
              <div key={u.id} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-sm font-semibold">
                    {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{u.full_name}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </div>
                  {getStatusBadge(u)}
                  {u.role === 'admin' && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Admin</span>
                  )}
                </div>
                <button
                  onClick={() => handleDepart(u.id)}
                  className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                >
                  {t('institutional.departAction')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Departed Users */}
      {departedUsers.length > 0 && (
        <div className="bg-white rounded-lg border border-amber-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-amber-200 bg-amber-50">
            <h3 className="text-sm font-medium text-amber-700">{t('institutional.departedEmployees')} ({departedUsers.length})</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {departedUsers.map(u => (
              <div key={u.id} className="px-5 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-sm font-semibold">
                      {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{u.full_name}</div>
                      <div className="text-xs text-gray-500">
                        {u.departed_at && t('institutional.departedOn', { date: new Date(u.departed_at).toLocaleDateString() })}
                      </div>
                    </div>
                    {getStatusBadge(u)}
                    {u.succeeded_by && (
                      <span className="text-xs text-gray-400">{t('institutional.dataTransferred')}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleReinstate(u.id)}
                      className="text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      {t('institutional.reinstateAction')}
                    </button>
                    {!u.succeeded_by && (
                      <button
                        onClick={() => setSuccessionUserId(successionUserId === u.id ? null : u.id)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {t('institutional.transferAction')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Succession panel */}
                {successionUserId === u.id && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 mb-2">{t('institutional.transferDescription')}</p>
                    <div className="flex items-center gap-3">
                      <select
                        value={successorId}
                        onChange={e => setSuccessorId(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      >
                        <option value="">{t('institutional.selectSuccessor')}</option>
                        {possibleSuccessors.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.user_type === 'institution'
                              ? `${s.full_name} (${t('institutional.sharedCompany')})`
                              : s.full_name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleTransferSuccession}
                        disabled={!successorId || isTransferring}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                      >
                        {isTransferring ? t('common.loading') : t('institutional.transferButton')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Succession Log */}
      {stats?.succession_log && stats.succession_log.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700">{t('institutional.successionLog')}</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {stats.succession_log.map((entry: SuccessionLogEntry) => (
              <div key={entry.id} className="px-5 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <span>
                    <span className="font-medium">{entry.departed_name}</span>
                    {' → '}
                    <span className="font-medium">{entry.successor_name}</span>
                  </span>
                  <span className="text-gray-500">{new Date(entry.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {entry.files_transferred} {t('institutional.files')}, {entry.tasks_transferred} {t('institutional.tasks')}
                  {entry.performed_by_name && ` — ${t('institutional.by')} ${entry.performed_by_name}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {activeUsers.length === 0 && departedUsers.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          {t('institutional.noEmployees')}
        </div>
      )}
    </div>
  );
}
